import { useCallback, useEffect, useState } from "react";
import { Education } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import useController from "./controller";
import { validateFields } from "../../../../lib/validation";
import { useTranslation } from "../../../../locales";
import { FieldInterface, FormValues, OptionItem } from "../../../../types";

export default function useViewController({
  educationIndex,
  educationValues,
  onChange,
  setCheckValidation,
}: {
  educationIndex: number;
  educationValues?: Education;
  onChange: (education: Education) => void;
  setCheckValidation: (key: number, cb: () => boolean) => void;
}) {
  const { t } = useTranslation();
  const [isStillHere, setIsStillHere] = useState(educationValues?.to?.month === undefined);

  const monthOptions: OptionItem[] = [
    { text: t("January"), value: "0" },
    { text: t("February"), value: "1" },
    { text: t("March"), value: "2" },
    { text: t("April"), value: "3" },
    { text: t("May"), value: "4" },
    { text: t("June"), value: "5" },
    { text: t("July"), value: "6" },
    { text: t("August"), value: "7" },
    { text: t("September"), value: "8" },
    { text: t("October"), value: "9" },
    { text: t("November"), value: "10" },
    { text: t("December"), value: "11" },
  ];

  const yearOptions: OptionItem[] = (() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Array(100)).map((i, index) => ({
      text: String(currentYear - index),
      value: currentYear - index,
    }));
  })();

  const educationTemplateFields: FieldInterface[] = [
    {
      name: `university-${educationIndex}`,
      type: "text",
      className: "experience-view-field__job-title",
      label: t("University"),
      validation: {
        required: true,
      },
    },
    {
      name: `degree-${educationIndex}`,
      type: "text",
      label: t("Degree"),
      className: "experience-view-field__firm",
      validation: {
        required: true,
      },
    },
    {
      name: `location-${educationIndex}`,
      type: "text",
      label: t("Location"),
      className: "experience-view-field__location",
      validation: {
        required: true,
      },
    },
    {
      name: `fromMonth-${educationIndex}`,
      type: "select",
      label: t("From"),
      placeholder: t("Month"),
      className: "experience-view-field__from-month",
      options: monthOptions,
      validation: {
        required: true,
      },
    },
    {
      name: `fromYear-${educationIndex}`,
      type: "select",
      label: t("From"),
      placeholder: t("Year"),
      className: "experience-view-field__from-year",
      labelClassName: "experience-view-field__label",
      options: yearOptions,
      validation: {
        required: true,
      },
    },
    {
      name: `toMonth-${educationIndex}`,
      type: "select",
      label: t("To"),
      placeholder: t("Month"),
      className: "experience-view-field__to-month",
      options: [{ text: t("I still study here"), value: undefined }, ...monthOptions],
    },
  ];
  if (!isStillHere) {
    educationTemplateFields.push({
      name: `toYear-${educationIndex}`,
      type: "select",
      label: t("To"),
      placeholder: t("Year"),
      className: "experience-view-field__to-year",
      labelClassName: "experience-view-field__label",
      options: yearOptions,
      validation: {
        required: true,
      },
    });
  }

  const { fields, values, setValues, errors, setErrors } = useController(
    educationTemplateFields,
    educationValues
      ? {
          [`university-${educationIndex}`]: educationValues.university,
          [`degree-${educationIndex}`]: educationValues.degree,
          [`location-${educationIndex}`]: educationValues.location,
          [`fromMonth-${educationIndex}`]: educationValues.from?.month,
          [`fromYear-${educationIndex}`]: educationValues.from?.year,
          [`toMonth-${educationIndex}`]: educationValues.to?.month,
          [`toYear-${educationIndex}`]: educationValues.to?.year,
        }
      : {}
  );

  const checkValidation = useCallback((): boolean => {
    const validation = validateFields(fields, values);
    if (validation.valid) {
      return true;
    } else {
      setErrors(validation.errors);
      return false;
    }
  }, [fields, setErrors, values]);

  useEffect(() => {
    setCheckValidation(educationIndex, checkValidation);
  }, [checkValidation, setCheckValidation, educationIndex]);

  const onChangeHandler = (values: FormValues) => {
    const education: Education = {
      university: values[`university-${educationIndex}`],
      degree: values[`degree-${educationIndex}`],
      location: values[`location-${educationIndex}`],
      from: {
        month: values[`fromMonth-${educationIndex}`],
        year: values[`fromYear-${educationIndex}`],
      },
      ...(values[`toMonth-${educationIndex}`] !== undefined
        ? { to: { month: values[`toMonth-${educationIndex}`], year: values[`toYear-${educationIndex}`] } }
        : {}),
    };
    setIsStillHere(education.to?.month === undefined);
    onChange(education);
    setValues(values);
  };

  return { fields, values, errors, setErrors, onChangeHandler };
}
